import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Eye, EyeOff, AlertCircle } from 'lucide-react';
import { setAdminToken } from '../lib/adminApi';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_accretion-axiom/artifacts/3rwpkqcm_7a40f4_da18f8518e4c4d5dbeedf37582cf828c~mv2.avif';

const AdminLoginPage = () => {
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/admin/auth`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password })
      });

      if (response.ok) {
        const data = await response.json();
        setAdminToken(data.token);
        sessionStorage.setItem('aac_admin_auth', 'true');
        navigate('/quantitative/results/admin');
      } else {
        setError('Incorrect password');
      }
    } catch (err) {
      setError('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-950 flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <a href="/" className="inline-block">
            <img 
              src={LOGO_URL} 
              alt="Accretion Axiom" 
              className="h-12 mx-auto"
            />
          </a>
          <p className="text-gray-500 text-sm mt-4 tracking-wider">ADMIN ACCESS</p>
        </div>

        {/* Login Card */}
        <div className="bg-gray-900 border border-gray-800 rounded-2xl p-8">
          <div className="flex items-center justify-center mb-6">
            <div className="w-14 h-14 bg-cyan-500/20 rounded-full flex items-center justify-center">
              <Lock size={28} className="text-cyan-400" />
            </div>
          </div>

          <h1 className="text-2xl font-bold text-white text-center mb-2">Results Dashboard</h1>
          <p className="text-gray-400 text-center mb-8">Enter password to access admin panel</p>

          <form onSubmit={handleSubmit}>
            <div className="relative mb-6">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 pr-12 text-white placeholder-gray-500 focus:border-cyan-500 focus:outline-none transition-colors"
                autoFocus
                data-testid="admin-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 transition-colors"
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>

            {error && (
              <div className="flex items-center gap-2 text-red-400 text-sm mb-4 bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-3">
                <AlertCircle size={18} />
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading || !password}
              className="w-full bg-cyan-500 hover:bg-cyan-400 disabled:bg-gray-700 disabled:text-gray-500 text-black font-semibold py-3 rounded-lg transition-colors flex items-center justify-center gap-2"
              data-testid="admin-login-btn"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin"></div>
              ) : (
                'Access Dashboard'
              )}
            </button>
          </form>
        </div>

        {/* Back link */}
        <div className="text-center mt-6">
          <a href="/quantitative/results" className="text-gray-500 hover:text-cyan-400 text-sm transition-colors">
            ← Back to Results
          </a>
        </div>
      </div>
    </div>
  );
};

export default AdminLoginPage;
